// src/pages/healthcare/appointments/components/RescheduleConfirmModal.tsx
import React, { useEffect, useState } from "react";
import { Alert, Modal, Typography } from "antd";

import { fetchPatientsByIds } from "../../../../api/healthcare/PatientApi";
import { PendingReschedule } from "../../../../hooks/appointment/useAppointmentMove";
import { dayjs } from "../../../../utils/time";
import type { UserUuid } from "../../../../types/identity";

const { Text } = Typography;

interface RescheduleConfirmModalProps {
	pending: PendingReschedule;
	practitionerName: (id: UserUuid) => string;
	busy: boolean;
	onConfirm: () => void;
	onCancel: () => void;
}

const SLOT_FORMAT = "ddd D MMM YYYY, h:mm A";

const slotLabel = (start: Date | string | undefined, end: Date | string | undefined): string =>
	`${dayjs(start).format(SLOT_FORMAT)} – ${dayjs(end).format("h:mm A")}`;

const minutesBetween = (start: Date | string | undefined, end: Date | string | undefined): number => dayjs(end).diff(dayjs(start), "minute");

// A drag is an easy misclick and the write reaches the patient's reminders, so every drop is confirmed.
// ⚠️ Deliberately has no "don't ask again".
const RescheduleConfirmModal: React.FC<RescheduleConfirmModalProps> = ({ pending, practitionerName, busy, onConfirm, onCancel }) => {
	const { appointment, blocks } = pending;
	const [patient, setPatient] = useState<string | null>(null);

	useEffect(() => {
		let live = true;

		if (appointment.patient_id) {
			fetchPatientsByIds([appointment.patient_id])
				.then((patients) => {
					const match = patients?.[0];
					if (live && match) setPatient(`${match.first_name} ${match.last_name}`.trim());
				})
				.catch(() => undefined);
		}

		return () => {
			live = false;
		};
	}, [appointment.patient_id]);

	const practitionerChanged = appointment.practitioner_id !== pending.practitionerId;
	const durationChanged = minutesBetween(appointment.start_time, appointment.end_time) !== minutesBetween(pending.start, pending.end);
	const overriding = blocks !== null;

	return (
		<Modal
			open
			title={overriding ? "Practitioner marked unavailable" : "Move this appointment?"}
			okText={overriding ? "Book over it anyway" : "Move appointment"}
			okButtonProps={{ danger: overriding, disabled: busy, "data-testid": "appointments-reschedule-accept" } as never}
			cancelButtonProps={{ disabled: busy, "data-testid": "appointments-reschedule-cancel" } as never}
			onOk={onConfirm}
			onCancel={onCancel}
			maskClosable={!busy}
			closable={!busy}
			width={520}
		>
			<div data-testid="appointments-reschedule-confirm">
				<Text strong>{patient || appointment.title || "This patient"}</Text>

				<div style={{ margin: "8px 0" }}>
					<div data-testid="appointments-reschedule-from" style={{ color: "var(--mg-text-secondary)" }}>
						{slotLabel(appointment.start_time, appointment.end_time)}
					</div>
					<div data-testid="appointments-reschedule-to">
						<Text strong>→ {slotLabel(pending.start, pending.end)}</Text>
					</div>
				</div>

				{/* Week · all staff can move an appointment belonging to a practitioner the user was not looking at. */}
				{practitionerChanged && (
					<div data-testid="appointments-reschedule-practitioner" style={{ marginBottom: 8 }}>
						Practitioner: {practitionerName(appointment.practitioner_id)} → <Text strong>{practitionerName(pending.practitionerId)}</Text>
					</div>
				)}

				{durationChanged && (
					<div data-testid="appointments-reschedule-duration" style={{ marginBottom: 8 }}>
						Duration: {minutesBetween(appointment.start_time, appointment.end_time)} min → <Text strong>{minutesBetween(pending.start, pending.end)} min</Text>
					</div>
				)}

				{overriding && (
					<Alert
						type="warning"
						showIcon
						style={{ marginBottom: 8 }}
						data-testid="appointments-reschedule-blocks"
						message="This time overlaps time the practitioner is marked unavailable"
						description={
							<ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
								{blocks?.map((block, index) => (
									<li key={index}>
										{dayjs(block.starts_at).format(SLOT_FORMAT)} – {dayjs(block.ends_at).format("h:mm A")}
										{block.reason ? ` — ${block.reason}` : ""}
									</li>
								))}
							</ul>
						}
					/>
				)}

				<Text type="secondary">This save also:</Text>
				<ul style={{ margin: "4px 0 0", paddingLeft: 18, color: "var(--mg-text-secondary)" }}>
					<li>reschedules the patient's appointment reminders and follow-ups</li>
					<li>re-assigns any tandem practitioner to the new time and notifies them</li>
					{appointment.cliniko_id ? <li data-testid="appointments-reschedule-cliniko">pushes the new time to Cliniko</li> : null}
					<li>does not email the patient a new confirmation</li>
				</ul>

				{busy && (
					<div data-testid="appointments-reschedule-busy" style={{ marginTop: 8 }}>
						<Text type="secondary">Moving the appointment…</Text>
					</div>
				)}
			</div>
		</Modal>
	);
};

export default RescheduleConfirmModal;
